import {
  PermissionResponse,
  RolePrivilegesResponse,
  RolesListingResponse,
  RolesResponse,
  userPermissionResponse,
  UsersResponse,
} from '@/types/rolesTypes';
import { UserResponse } from '../slices/userSlice';
import apiSlice from './apiSlice';

const rolesAndPrivilegesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRoles: builder.query<
      RolesResponse,
      {
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ page, limit, search }) => ({
        url: '/role/all-roles',
        method: 'GET',
        params: {
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(search ? { search } : {}),

          ts: Date.now(),
        },
      }),
    }),
    createRole: builder.mutation<
      any,
      {
        name: string;
        description: string;
        privilegesWithAccess: {
          privilegeId: number;
          access: Record<string, boolean>;
        }[];
      }
    >({
      query: (body) => ({
        url: '/role',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members'],
    }),
    getPrivileges: builder.query<
      PermissionResponse,
      {
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ page, limit, search }) => ({
        url: '/role/privileges',
        method: 'GET',
        params: {
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(search ? { search } : {}),

          ts: Date.now(),
        },
      }),
    }),
    getRolesListing: builder.query<RolesListingResponse[], {}>({
      query: () => ({
        url: '/role/roles',
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
    }),
    getAccessUsers: builder.query<
      UsersResponse,
      {
        search?: string;
        page?: number;
        limit?: number;
        fromDate?: string;
        toDate?: string;
      }
    >({
      query: ({ page, limit, search, fromDate, toDate }) => ({
        url: '/privileges/all/subadmin/user',
        method: 'GET',
        params: {
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ts: Date.now(),
        },
      }),
      providesTags: ['accessMember', 'Members'],
    }),
    deleteRole: builder.mutation<any, number>({
      query: (id) => ({
        url: `/role/${id}`,
        method: 'DELETE',
      }),
    }),
    deleteAccessUser: builder.mutation<any, number>({
      query: (id) => ({
        url: `/privileges/delete/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['accessMember'],
    }),
    getRoleDetails: builder.query<RolePrivilegesResponse, number>({
      query: (id) => ({
        url: `/role/${id}/privileges`,
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
    }),
    updateRolesPermission: builder.mutation<
      any,
      {
        id: number;
        name: string;
        description: string;
        privilegesWithAccess: {
          privilegeId: number;
          access: Record<string, boolean>;
        }[];
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/role/${id}`,
        method: 'PATCH',
        body,
      }),
    }),
    getAccessDetails: builder.query<UserResponse, number>({
      query: (id) => ({
        url: `/privileges/${id}`,
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
    }),
    getUserRolesDetails: builder.query<userPermissionResponse, number>({
      query: (id) => ({
        url: '/privileges/subadmin/detail',
        method: 'GET',
        params: {
          ts: Date.now(),
        },
      }),
    }),
    changeRoleUserStatus: builder.mutation<any, { userId: number; status: string }>({
      query: ({ userId, status }) => ({
        url: `/privileges/change-status/${userId}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['Members'],
    }),
    updateAccessPermission: builder.mutation<
      any,
      {
        id: string | number;
      } & Partial<{
        firstname: string;
        lastname: string;
        email: string;
        username: string;
        mobile: string;
        roleId: number;
        password: string;
        transactionCode: string;
        fromAccount: string;
        partnership: number;
        isRole?: number;
      }>
    >({
      query: ({ id, ...body }) => ({
        url: `/privileges/patch-by-admin/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['UserSummary'],
    }),
    roleUserChangePassword: builder.mutation<
      any,
      {
        id: string;
        newPassword: string;
        transactionCode: string;
      }
    >({
      query: ({ id, newPassword, transactionCode }) => ({
        url: `/privileges/change-password/${id}`,
        // url: `/admin/users/password/${id}`,
        method: 'PATCH',
        body: { newPassword, transactionCode },
      }),
    }),
  }),
});
export const {
  useGetRolesQuery,
  useCreateRoleMutation,
  useGetPrivilegesQuery,
  useGetRolesListingQuery,
  useGetAccessUsersQuery,
  useDeleteRoleMutation,
  useDeleteAccessUserMutation,
  useGetRoleDetailsQuery,
  useUpdateRolesPermissionMutation,
  useGetAccessDetailsQuery,
  useGetUserRolesDetailsQuery,
  useChangeRoleUserStatusMutation,
  useUpdateAccessPermissionMutation,
  useRoleUserChangePasswordMutation,
} = rolesAndPrivilegesApi;
